/**
 * The Simple page's Upload bar: the one thing above the fold.
 *
 * Pick or drop a file and it uploads straight away (signed out, the sign-in sheet opens over the
 * page first and the upload starts the moment it closes, so the File is never lost). The bar turns
 * into a progress bar, and when the upload lands we open My Library, where the new video sits on
 * top and opens when its clips are ready. Same upload store and endpoints as every other uploader.
 */
import React, { useCallback, useContext, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { userContext } from "../../../../stores/users/userContext";
import { messageContext } from "../../../../stores/messages/messageContext";
import { importFromUrlEndpoint } from "../../../constants";
import apiRequest from "../../../../utils/apiClient";
import { limitLink } from "../../../../utils/limitErrorHandler";
import { ROUTES } from "../../../../constants";
import { useFileUpload } from "../hooks/useFileUpload";
import { LANGUAGE_CHOICES } from "../components/UploadDownloadArea";
import { LANGUAGE_NAMES } from "../../../../constants/languages";
import { ICON } from "../components/home3/brandIcons";
import { SIMPLE_HERO, SIMPLE_LANG, SIMPLE_PROGRESS, SIMPLE_SIGNIN } from "../constants/simpleHomeText";

/** Shown first in the language menu, then every other language A-Z. */
const POPULAR = ["en", "he", "yi", "es", "fr", "ru", "ar"];
const ALL_CODES = Object.keys(LANGUAGE_NAMES).sort((x, y) => LANGUAGE_NAMES[x].localeCompare(LANGUAGE_NAMES[y]));
/** "auto" → [] (detect), "mix" → the three, a language code → just that one. */
const langsFor = (id: string): string[] => {
  const chip = LANGUAGE_CHOICES.find((c) => c.id === id);
  if (chip) return chip.langs;
  return LANGUAGE_NAMES[id] ? [id] : [];
};

const ACCEPT = "video/*,audio/*,.mp3,.m4a,.wav,.aac,.flac,.ogg,.oga,.opus,.aiff,.aif,.wma,.amr,.3gp,.webm";

interface Props {
  /** Open the sign-in sheet with a line on top; `then` runs once they are signed in. */
  requireSignIn: (note: string, then: () => void) => void;
}

const SimpleUpload: React.FC<Props> = ({ requireSignIn }) => {
  const navigate = useNavigate();
  const userStore = useContext(userContext);
  const messageStore = useContext(messageContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const [hot, setHot] = useState(false);
  const [done, setDone] = useState(false);
  const [langId, setLangId] = useState("auto");
  const [link, setLink] = useState("");
  const [importing, setImporting] = useState(false);
  const [linkMsg, setLinkMsg] = useState<string | null>(null);

  const up = useFileUpload(
    () => undefined,
    (error, limit) => messageStore.setMessage(error, "error", limit ? 20000 : 6000, limit),
  );

  const openLibrary = useCallback(() => {
    setDone(true);
    window.setTimeout(() => navigate(ROUTES.MY_LIBRARY_UPLOADS), 900);
  }, [navigate]);

  const applyLanguage = useCallback(() => {
    up.setLanguages(langsFor(langId));
  }, [langId, up]);

  const start = useCallback(
    async (files: File[]) => {
      applyLanguage();
      if (files.length > 1) {
        await up.uploadBatch(files, openLibrary);
      } else if (await up.uploadFile()) {
        openLibrary();
      }
    },
    [applyLanguage, up, openLibrary],
  );

  const take = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) return;
      const files = Array.from(list).filter((f) => up.validateFile(f).isValid);
      if (files.length === 0) {
        up.handleFileSelect(list[0]); // shows the "not a video" message
        return;
      }
      up.handleFileSelect(files[0]);
      if (userStore.isUserAuth()) {
        start(files);
      } else {
        requireSignIn(SIMPLE_SIGNIN.fileReady(files.length > 1 ? `${files.length} videos` : files[0].name), () => start(files));
      }
    },
    [up, userStore, start, requireSignIn],
  );

  const importLink = useCallback(async () => {
    const url = link.trim();
    if (!url || importing) return;
    setImporting(true);
    setLinkMsg(null);
    try {
      const res = await apiRequest(importFromUrlEndpoint, {
        method: "POST",
        body: JSON.stringify({ url, transcription_engine: up.transcriptionEngine, languages: langsFor(langId) }),
        headers: { "Content-Type": "application/json" },
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setLink("");
        navigate(ROUTES.MY_LIBRARY_UPLOADS);
      } else if (res.status === 403 && body.upgrade) {
        messageStore.setMessage(body.error || "Upload limit exceeded.", "error", 20000, limitLink(body));
      } else {
        setLinkMsg(body.message || "Couldn't import that link.");
      }
    } catch (e) {
      setLinkMsg("Couldn't reach the server. Please try again.");
    } finally {
      setImporting(false);
    }
  }, [link, importing, up.transcriptionEngine, langId, navigate, messageStore]);

  const onImport = () => {
    if (!link.trim()) return;
    if (userStore.isUserAuth()) importLink();
    else requireSignIn(SIMPLE_SIGNIN.linkReady, importLink);
  };

  if (up.isUploading || done) {
    const pct = done ? 100 : Math.round(up.uploadProgress || 0);
    const name = up.file?.name || "";
    const line = done
      ? SIMPLE_PROGRESS.done
      : up.uploadFinishing
        ? SIMPLE_PROGRESS.finishing
        : up.uploadEtaSeconds
          ? `${SIMPLE_PROGRESS.eta(up.uploadEtaSeconds)}. ${SIMPLE_PROGRESS.next}`
          : `${SIMPLE_PROGRESS.uploading} ${SIMPLE_PROGRESS.next}`;
    return (
      <div className="prog" role="status" aria-live="polite">
        <div className="row">
          <span className="name">
            {up.batchTotal > 1 ? `${SIMPLE_PROGRESS.batch(up.batchCurrent, up.batchTotal)} · ` : ""}
            {name}
          </span>
          <span className="pct">{pct}%</span>
        </div>
        <div className="track">
          <i style={{ width: `${pct}%` }} />
        </div>
        <p>{line}</p>
      </div>
    );
  }

  return (
    <>
      <div
        className={`bar${hot ? " hot" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onMouseEnter={() => setHot(true)}
        onMouseLeave={() => setHot(false)}
        onDragOver={(e) => {
          e.preventDefault();
          setHot(true);
        }}
        onDragLeave={() => setHot(false)}
        onDrop={(e) => {
          e.preventDefault();
          setHot(false);
          take(e.dataTransfer.files);
        }}
      >
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M12 16V4m0 0l-5 5m5-5l5 5M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
        <div className="drop">
          <b>{hot ? SIMPLE_HERO.dropping : SIMPLE_HERO.drop}</b>&nbsp; {SIMPLE_HERO.dropOr}
        </div>
        <button type="button" className="up" tabIndex={-1}>
          {SIMPLE_HERO.upload}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: "none" }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            take(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {/* The link box is always open: one less click (Isaac, 10-25). A Drive / Dropbox link
          only needs one setting, said right under it. */}
      <form
        className="paste"
        onSubmit={(e) => {
          e.preventDefault();
          onImport();
        }}
      >
        <input
          type="url"
          inputMode="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder={SIMPLE_HERO.pastePlaceholder}
          aria-label={SIMPLE_HERO.pastePlaceholder}
        />
        <button type="submit" disabled={!link.trim() || importing}>
          {importing ? SIMPLE_HERO.pasteBusy : SIMPLE_HERO.pasteGo}
        </button>
      </form>
      {/* Fixed height, so an error appearing doesn't push the page down. */}
      <p className={`paste-msg${linkMsg ? " bad" : ""}`}>
        {linkMsg || (
          <>
            <img src="/media/brand/google-drive.svg" alt="" className="src-mark" />
            <svg viewBox="0 0 24 24" className="src-mark" aria-hidden>
              <path fill="#0061FF" d={ICON.dropbox} />
            </svg>
            {SIMPLE_HERO.pasteHint}
          </>
        )}
      </p>
      <div className="under">
        <label>
          {SIMPLE_HERO.language}:{" "}
          <select value={langId} onChange={(e) => setLangId(e.target.value)} aria-label={SIMPLE_HERO.language}>
            <option value="auto">{SIMPLE_LANG.auto}</option>
            <option value="mix">{SIMPLE_LANG.mixed}</option>
            <optgroup label={SIMPLE_LANG.popular}>
              {POPULAR.map((c) => (
                <option key={c} value={c}>
                  {LANGUAGE_NAMES[c]}
                </option>
              ))}
            </optgroup>
            <optgroup label={SIMPLE_LANG.all(ALL_CODES.length)}>
              {ALL_CODES.filter((c) => !POPULAR.includes(c)).map((c) => (
                <option key={c} value={c}>
                  {LANGUAGE_NAMES[c]}
                </option>
              ))}
            </optgroup>
          </select>
        </label>
      </div>
    </>
  );
};

export default observer(SimpleUpload);
